import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Stack, FormControl, Select, MenuItem, Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import BoardList from '@components/Board/BoardList.jsx';
import BoardListCard from '@components/Board/BoardListCard.jsx';
import BasicModal from '@components/Modal/BasicModal.jsx';
import { useIsMobile } from '@/hooks/Responsive';
import { getXrCntsList } from '@/api/vx';
import AlertConfirmModal from '@components/Modal/AlertConfirmModal.jsx';
import { useAuth } from '@/components/AuthContext'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { visuallyHidden } from '@mui/utils'
import { cmmSend } from 'api/comp';
import { deleteMyContentsConcern, insertMyContentsConcern } from '@/api/mypage';
import { useConfirm } from '@/components/ConfirmProvider';

export function XRToolbar({
                            isMobile,
                            category,
                            subCategory,
                            detailCategory,
                            onCategoryChange,
                            onSubCategoryChange,
                            onDetailCategoryChange,
                            mainCtgrData,
                            midCtgrData,
                            smallCtgrData,
                          }) {
  return (
      <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={1}
          sx={{ flex: 1, width: '100%' }}
      >
        {/* 대카테고리 */}
        <FormControl sx={{ minWidth: '150px' }}>
          <Typography
              component="label"
              sx={visuallyHidden}
              htmlFor="main-category"
          >
            대카테고리 선택
          </Typography>
          <Select
              displayEmpty
              labelId="main-category-label"
              value={category}
              onChange={(e) => onCategoryChange(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: '100%',
                    maxWidth: (theme) =>
                        isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
                  },
                },
              }}
              inputProps={{
                id: 'main-category',
              }}
              sx={{
                width: '100%',
                maxWidth: (theme) =>
                    isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
              }}
          >
            <MenuItem value="">대카테고리</MenuItem>
            {mainCtgrData?.map((b) => (
                <MenuItem key={b.lcmsTaxonomyId} value={b.lcmsTaxonomyId}>
                  {b.lcmsTaxonomyName}
                </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 중카테고리 */}
        <FormControl sx={{ minWidth: '150px' }}>
          <Typography
              component="label"
              sx={visuallyHidden}
              htmlFor="sub-category"
          >
            중카테고리 선택
          </Typography>
          <Select
              displayEmpty
              value={subCategory}
              onChange={(e) => onSubCategoryChange(e.target.value)}
              disabled={!category}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: '100%',
                    maxWidth: (theme) =>
                        isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
                  },
                },
              }}
              inputProps={{
                id: 'sub-category',
              }}
              sx={{
                width: '100%',
                maxWidth: (theme) =>
                    isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
              }}
          >
            <MenuItem value="">중카테고리</MenuItem>
            {midCtgrData?.map((m) => (
                <MenuItem key={m.lcmsTaxonomyId} value={m.lcmsTaxonomyId}>
                  {m.lcmsTaxonomyName}
                </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 소카테고리 */}
        <FormControl sx={{ minWidth: '150px' }}>
          <Typography
              component="label"
              sx={visuallyHidden}
              htmlFor="detail-category"
          >
            소카테고리 선택
          </Typography>
          <Select
              displayEmpty
              value={detailCategory}
              onChange={(e) => onDetailCategoryChange(e.target.value)}
              disabled={!subCategory}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: '100%',
                    maxWidth: (theme) =>
                        isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
                  },
                },
              }}
              inputProps={{
                id: 'detail-category',
              }}
              sx={{
                width: '100%',
                maxWidth: (theme) =>
                    isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
              }}
          >
            <MenuItem value="">소카테고리</MenuItem>
            {smallCtgrData?.map((s) => (
                <MenuItem key={s.lcmsTaxonomyId} value={s.lcmsTaxonomyId}>
                  {s.lcmsTaxonomyName}
                </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
  );
}

export default function XRContents() {
  const navigate = useNavigate();
  const location = useLocation()
  const { isMobile } = useIsMobile();

  const [openPreview, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [iframeKey, setIframeKey] = useState(0) // iframe 재생성

  const [confirmedSearch, setConfirmedSearch] = useState('');

  const [mainCtgrData, setMainCtgrData]   = useState([]);   // 분류체계-대분류
  const [midCtgrData, setMidCtgrData]     = useState([]);   // 분류체계-중분류
  const [smallCtgrData, setSmallCtgrData] = useState([]);   // 분류체계-소분류

  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [detailCategory, setDetailCategory] = useState('');

  const [confirmedCategory, setConfirmedCategory] = useState('');
  const [confirmedSubCategory, setConfirmedSubCategory] = useState('');
  const [confirmedDetailCategory, setConfirmedDetailCategory] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmConcernOpen, setConfirmConcernOpen] = useState(false)
  const [itemToToggle, setItemToToggle] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [modalDisplayItem, setModalDisplayItem] = useState(null);

  const { alert } = useConfirm();

  const { user, isAuthReady, isAuthenticated } = useAuth();

  const lang = useLang()


  const handleCategoryChange = (val) => {
    setCategory(val);
    setSubCategory('');
    setDetailCategory('');
  };

  const handleSubCategoryChange = (val) => {
    setSubCategory(val);
    setDetailCategory('');
  };

  const handleDetailCategoryChange = (val) => {
    setDetailCategory(val);
  };

  const handlePreviewClick = useCallback((row) => {
    setSelected(row);
    setPreviewOpen(true);
  }, []);

  const handleClosePreview = () => {
    setPreviewOpen(false)
    setIframeKey(prev => prev + 1)
  }

  const handleConfirmAccept = () => {
    setConfirmOpen(false)
    // navigate('/login')
    //navigate(buildLangHref('/login', lang))
    navigate(buildLangHref('/login', lang), {
      state: {
        redirectTo: location.pathname + location.search,
      },
    })
  }

  const handleDetailClick = (row) => {
    // console.log('XR Contents Detail Clicked:', row);
    // navigate(`/contents/xrview/${row.id}`, { state: { row } });
    navigate(buildLangHref(`/contents/xrview/${row.id}`, lang), { state: { row } });
  };

  const fetcher = useCallback(
      async ({ page = 1, pageSize = 6, recordSize = 6 }) => {
        const body = {
          page,
          pageSize,
          recordSize,
          cmsGrandDivCode: confirmedCategory,
          cmsMidiumDivCode: confirmedSubCategory,
          cmsSmallDivCode: confirmedDetailCategory,
          keyword: confirmedSearch,
          accountId: user?.accountId,
        };

        const res = await getXrCntsList(body);
        const pageData = res?.data;
        const items = pageData?.content ?? [];

        const formattedItems = items.map((item, index) => ({
          id: item.cmsContentsId,
          title: item.cmsContentsName,
          // xr: index % 2 === 0 ? '체험형XR' : '실습형XR', // XR 유형 (예시)
          xr: item?.contentType,
          preview: true,  // 미리보기 여부 (예시)
          interest: item?.concernYn === 'Y',   // 관심콘텐츠 여부 (예시)
          // Cat: '대카테고리' ?? item.grandDivCode,  // 대분류 카테고리 (코드명으로 변경 필요)
          // subCat: '중카테고리' ?? item.midiumDivCode, // 중분류 카테고리 (코드명으로 변경 필요)
          // detailCat: '' ?? item.smallDivCode, // 소분류 카테고리 (코드명으로 변경 필요)
          category: item.lcmsTaxonomyFullPath,
          // previewUrl: 'https://youtube.com/embed/', // 미리보기 URL (예시)
          //previewUrl: item.cmsMediaFilePath,
          previewUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/portal/contents/OVL-2025-010107-0001/preview/preview2.mp4', //추후 경로가 정해지면 변경예정
          thumbUrl: item?.thumbnailImgUrl, /* || imgArr[Math.floor(Math.random() * imgArr.length)].thumbUrl, */// 썸네일 URL - 확정시 변경필요
        }));

        return {
          items: formattedItems,
          total: pageData?.totalElements ?? items.length,
        };
      },
      [
        confirmedSearch,
        confirmedCategory,
        confirmedSubCategory,
        confirmedDetailCategory,
        fetchKey,
      ],
  );

  const [interest, setInterest] = useState(false);

  // const [interestModalOpen, setInterestModalOpen] = useState(false);
  // const [interestMessage, setInterestMessage] = useState('');

  //관심컨텐츠 이벤트 관련
  const toggleInterest = useCallback((row) => {
    setItemToToggle(row);
    if(!isAuthenticated) setConfirmOpen(true);
    else {
      setModalDisplayItem(row);
      setConfirmConcernOpen(true);
    }
  }, [isAuthenticated]);

  const handleConfirmToggle = useCallback(async () => {
    if (!itemToToggle) return;

    try {
      const params = {
        cmsContentsId: itemToToggle.id,
        accountId: user?.accountId
      };
      if(itemToToggle.interest) {
        await deleteMyContentsConcern(params);
      } else {
        await insertMyContentsConcern(params);
      }
      alert(itemToToggle.interest ? "관심콘텐츠 목록에서 삭제되었습니다." : "관심콘텐츠 목록에 등록되었습니다.");
      // setData(null); // Invalidate cache to trigger re-fetch
      setFetchKey((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to delete interested content', error);
      // Here we could show an error alert to the user.
    } finally {

      setConfirmConcernOpen(false);
      setItemToToggle(null);
    }
  }, [itemToToggle, user]);

  /* 최초 대분류 정보 호출 */
  useEffect(() => {
    searchMainCmb();
  }, []);

  /* 대분류 변경시 중분류 호출 */
  useEffect(() => {
    searchMidCmb();
  }, [category]);

  /* 중분류 변경시 소분류 호출 */
  useEffect(() => {
    searchSmallCmb();
  }, [subCategory]);

  // 대분류 정보 조회
  const searchMainCmb = async () => {
    const res = await cmmSend('/com/getLcmsTaxonomyList', {lv:'1'});
    if (res?.data) {
      setMainCtgrData(res.data);
    }
  };

  // 중분류 정보 조회
  const searchMidCmb = async () => {
    setMidCtgrData([]);
    setSmallCtgrData([]);

    if (category) {
      const res = await cmmSend('/com/getLcmsTaxonomyList', {lv:'2', lcmsTaxonomyId:category});
      if (res?.data) {
        setMidCtgrData(res.data);
      }
    }
  };

  // 소분류 정보 조회
  const searchSmallCmb = async () => {
    setSmallCtgrData([]);
    if (subCategory) {
      const res = await cmmSend('/com/getLcmsTaxonomyList', {lv:'3', lcmsTaxonomyId:subCategory});
      if (res?.data) {
        setSmallCtgrData(res.data);
      }
    }
  };

  return (
      <>
        <BoardList
            view="card"
            columns={[]}
            fetcher={fetcher}
            initialPage={1}
            initialPageSize={6}
            initialSort={{ field: 'id', direction: 'desc' }}
            CardComponent={BoardListCard}
            storageKey="setpage_xr"
            CardProps={{
              gridCols: 3,
              onPreviewClick: handlePreviewClick,
              onDetailClick: handleDetailClick,
              onInterestClick: toggleInterest,
            }}
            toolbarLeft={
              <XRToolbar
                  isMobile={isMobile}
                  category={category}
                  subCategory={subCategory}
                  detailCategory={detailCategory}
                  onCategoryChange={handleCategoryChange}
                  onSubCategoryChange={handleSubCategoryChange}
                  onDetailCategoryChange={handleDetailCategoryChange}
                  mainCtgrData={mainCtgrData}
                  midCtgrData={midCtgrData}
                  smallCtgrData={smallCtgrData}
              />
            }
            onSearchSubmit={(searchValue) => {
              setConfirmedSearch(searchValue);
              setConfirmedCategory(category);
              setConfirmedSubCategory(subCategory);
              setConfirmedDetailCategory(detailCategory);
            }}
            variant="content"
            emptyMessage="나의 관심콘텐츠가 없습니다."
        />
        <AlertConfirmModal
            open={confirmOpen}
            title="로그인이 필요한 서비스 입니다."
            description="로그인 페이지로 이동하겠습니까?"
            type="confirm"
            onConfirm={handleConfirmAccept}
            onClose={() => setConfirmOpen(false)}
            onCancel={() => setConfirmOpen(false)}
        />
        {/* <AlertConfirmModal
        open={interestModalOpen}
        title={interestMessage}
        type="alert"
        onConfirm={() => setInterestModalOpen(false)}
        onClose={() => setInterestModalOpen(false)}
      /> */}
        <AlertConfirmModal
            open={confirmConcernOpen}
            title={modalDisplayItem?.interest ? '관심컨텐츠 삭제' : '관심컨텐츠 등록'}
            description={modalDisplayItem?.interest ? `${modalDisplayItem?.title}\n해당 콘텐츠를 삭제하시겠습니까?` : `${modalDisplayItem?.title}\n해당 콘텐츠를 등록하시겠습니까?`}
            type="confirm"
            onConfirm={handleConfirmToggle}
            onClose={() => setConfirmConcernOpen(false)}
            onCancel={() => {
              setConfirmConcernOpen(false)
              setItemToToggle(null)
            }}
        />
        <BasicModal
            open={openPreview}
            onClose={handleClosePreview}
            title={selected?.title}
            disableEnforceFocus
            disablePortal
        >
          <Box
              sx={{
                position: 'relative',
                width: '100%',
                paddingTop: '56.25%',
                borderRadius: 2,
                overflow: 'hidden',
              }}
          >
            <Box
                key={iframeKey}
                component="iframe"
                src={selected?.previewUrl || null}
                title="video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
            />
          </Box>
        </BasicModal>
      </>
  );
}
